import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  AnalysisApiClientAccess,
  AppFeatureService,
  AppRoleService,
  ApplicationApiClientAccess,
} from '@nk/access-block';
import {
  MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN,
  MembershipGeoCountryApiService,
  SENSITIVE_DATA_API_SERVICE_TOKEN,
  SensitiveDataApiService,
} from '@nk/api-lib';
import { CachingService, StableCache } from '@nk/caching-lib';
import {
  AppFeatureKey,
  AppRoleKey,
  COUNTRY_FEATURES,
  CompanyEntity,
  CompanyEventAdminReviewEntity,
  CompanyEventBasicComposite,
  CompanyEventCommonAnalysis,
  CompanyEventCommonGroupingEntity,
  CompanyEventCommonMeasureEntity,
  CompanyEventCommonModelEntity,
  CompanyEventCommonModelPriorityEntity,
  CompanyEventCommonRuleEntity,
  CompanyEventComposite,
  CompanyEventEntity,
  CompanyEventTypeEntity,
  CompanyRiskScoreAnalysis,
  CompanyRiskScoreAnalysisListByModel,
  CompanyRiskScoreCompositeEntity,
  CompanyRiskScoreGroupingEntity,
  CompanyRiskScoreMeasureEntity,
  CompanyRiskScoreModelEntity,
  CompanyRiskScoreModelPriorityEntity,
  DataSourceCategoryComposite,
  DataSourceCategoryKey,
  DataSourceEntity,
  EventEntity,
  EventTypeEntity,
  ModelTypeEntity,
  SensitiveDataParams,
  SensitiveDataTypeKey,
  SimpleCollection
} from '@nk/data-entities-lib';
import { Dict, arrayToDict, arrayToDictList, dictToKeys, dictToValues, includes } from '@nk/utils-lib';
import { Observable, combineLatest, forkJoin, from, of, throwError } from 'rxjs';
import { concatMap, first, map, mergeMap, switchMap } from 'rxjs/operators';
import { EntityUpdateTimestampService } from './entity-update-timestamp.service';

export interface CompanyEventMeasureComposite {
  model?: CompanyEventCommonModelEntity;
  measure?: CompanyEventCommonMeasureEntity;
  grouping?: CompanyEventCommonGroupingEntity;
  model_type?: ModelTypeEntity;
  company_event: CompanyEventEntity;
  company: CompanyEntity;
  event: EventEntity;
}

export interface CompanyEventAnalysis {
  model: CompanyEventCommonModelEntity;
  measure: CompanyEventCommonMeasureEntity;
  grouping?: CompanyEventCommonGroupingEntity;
  rule?: CompanyEventCommonRuleEntity;
  priority?: CompanyEventCommonModelPriorityEntity;
  model_type?: ModelTypeEntity;
}

@Injectable()
export class RiskService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _applicationApiAccess: ApplicationApiClientAccess,
    private _cachingService: CachingService,
    private _appRoleService: AppRoleService,
    private _appFeatureService: AppFeatureService,
    @Inject(SENSITIVE_DATA_API_SERVICE_TOKEN) private _sensitiveDataService: SensitiveDataApiService,
    @Inject(MEMBERSHIP_GEO_COUNTRY_API_SERVICE_TOKEN)
    private _membershipGeoCountryService: MembershipGeoCountryApiService,
    private _entityUpdateTimestampService: EntityUpdateTimestampService,
  ) {}

  public companyEventCommonModelPriorityListCache = this._cachingService.create(
    this,
    this.fetchCompanyEventCommonModelPriorityList,
  );
  public fetchCompanyEventCommonModelPriorityList(): Observable<CompanyEventCommonModelPriorityEntity[]> {
    return this._analysisApiAccess.get<CompanyEventCommonModelPriorityEntity[]>('company-event-common-model-priority');
  }

  public companyEventCommonRuleListCache = this._cachingService.create(this, this.fetchCompanyEventCommonRuleList);
  public fetchCompanyEventCommonRuleList(): Observable<CompanyEventCommonRuleEntity[]> {
    return this._analysisApiAccess.get<CompanyEventCommonRuleEntity[]>('company-event-common-rule');
  }

  public companyRiskScoreModelListCache = new StableCache(
    'app.company_risk_score_model',
    this,
    this.fetchCompanyRiskScoreModelList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchCompanyRiskScoreModelList(): Observable<CompanyRiskScoreModelEntity[]> {
    return this._analysisApiAccess.get<CompanyRiskScoreModelEntity[]>('company-risk-score-model');
  }

  public companyRiskScoreModelPriorityListCache = new StableCache(
    'app.company_risk_score_model_priority',
    this,
    this.fetchCompanyRiskScoreModelPriorityList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchCompanyRiskScoreModelPriorityList(): Observable<CompanyRiskScoreModelPriorityEntity[]> {
    return this._analysisApiAccess.get<CompanyRiskScoreModelPriorityEntity[]>('company-risk-score-model-priority');
  }

  public companyRiskScoreGroupingListCache = this._cachingService.create(this, this.fetchCompanyRiskScoreGroupingList);
  public fetchCompanyRiskScoreGroupingList(): Observable<CompanyRiskScoreGroupingEntity[]> {
    return this._analysisApiAccess.get<CompanyRiskScoreGroupingEntity[]>('company-risk-score-grouping');
  }

  public resolveCompanyRiskScoreGroupingFromMeasure(
    measureValue: number,
    companyRiskScoreGrouping: CompanyRiskScoreGroupingEntity[],
  ): CompanyRiskScoreGroupingEntity {
    companyRiskScoreGrouping.sort((a, b) => a.to_measure_value - b.to_measure_value);
    const found = companyRiskScoreGrouping.find((a) => {
      if (a.to_measure_value) {
        return a.from_measure_value <= measureValue && a.to_measure_value >= measureValue;
      } else if (a.from_measure_value) {
        return a.from_measure_value <= measureValue;
      }
    });
    if (!found) {
      return companyRiskScoreGrouping.find((a) => a.group_key === 'unset');
    }
    return found;
  }

  // TODO: IMPORTANT, we need to actually check the *company* for sensitive data, or ignore it. Now it will succeeed on customer level.
  public companyEventCache = this._cachingService.create(this, this.fetchCompanyEvent);
  public fetchCompanyEvent(companyEventUuid: string, skipSensitiveDataClaim = false): Observable<CompanyEventEntity> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([
        AppFeatureKey.base,
        AppFeatureKey.events_news,
        AppFeatureKey.payment_remark,
        AppFeatureKey.norwegian_property_data,
        AppFeatureKey.company_risk_score,
        ...COUNTRY_FEATURES,
      ]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check, AppRoleKey.norwegian_property_data]),
      skipSensitiveDataClaim
        ? of([])
        : this._sensitiveDataService.claimValidSensitiveDataTypeKeysByParams(
            { companyEventUuid: companyEventUuid } as SensitiveDataParams,
            [SensitiveDataTypeKey.payment_remark],
          ),
    ]).pipe(
      switchMap(([validFeatureKeys, validRoleKeys, validSensitiveDataTypeKeys]) => {
        return this._analysisApiAccess.get<CompanyEventEntity>(
          `company-event/${companyEventUuid}`,
          {},
          validFeatureKeys,
          validRoleKeys,
          this.filterValidSensitiveDataClaims(validFeatureKeys, validSensitiveDataTypeKeys),
        );
      }),
    );
  }

  // public companyEventCompositeByCompanyEventCache = this._cachingService.create(this, this.fetchCompanyEventCompositeByCompanyEvent);
  public fetchCompanyEventCompositeByCompanyEvent(
    companyEventUuid: string,
    limit: number,
    getPrimaryOfEvent = false,
    getSecondaryOfEvent = false,
    includeDisabled = false,
    skipSensitiveDataClaim = false, // used for digest admin review (admin_flag)
  ): Observable<SimpleCollection<CompanyEventComposite>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('limit', limit);
    httpParams = httpParams.append('return_primary', String(getPrimaryOfEvent));
    httpParams = httpParams.append('return_secondary', String(getSecondaryOfEvent));
    httpParams = httpParams.append('include_disabled', String(includeDisabled));

    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([
        AppFeatureKey.base,
        AppFeatureKey.events_news,
        AppFeatureKey.payment_remark,
        AppFeatureKey.norwegian_property_data,
        AppFeatureKey.company_risk_score,
        ...COUNTRY_FEATURES,
      ]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check, AppRoleKey.norwegian_property_data]),
      skipSensitiveDataClaim
        ? of([])
        : this._sensitiveDataService.claimValidSensitiveDataTypeKeysByParams(
            { companyEventUuid: companyEventUuid } as SensitiveDataParams,
            [SensitiveDataTypeKey.payment_remark],
          ),
    ]).pipe(
      switchMap(([validFeatureKeys, validRoleKeys, validSensitiveDataTypeKeys]) => {
        return this._analysisApiAccess.get<SimpleCollection<CompanyEventComposite>>(
          `company-event/${companyEventUuid}/company-event-composite-collection`,
          { params: httpParams },
          validFeatureKeys,
          validRoleKeys,
          validSensitiveDataTypeKeys,
        );
      }),
    );
  }

  public dataSourceCache = this._cachingService.create(this, this.fetchDataSource);
  public fetchDataSource(dataSourceUuid: string): Observable<DataSourceEntity> {
    return this._analysisApiAccess.get<DataSourceEntity>(`data-source/${dataSourceUuid}`);
  }

  public dataSourceListCache = new StableCache(
    'app.data_source',
    this,
    this.fetchDataSourceList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchDataSourceList(): Observable<DataSourceEntity[]> {
    return this._analysisApiAccess.get<DataSourceEntity[]>('data-source');
  }

  public dataSourceCategoryCompositeListCache = this._cachingService.create(
    this,
    this.fetchDataSourceCategoryCompositeList,
  );
  public fetchDataSourceCategoryCompositeList(dataSourceDomainKey?: string): Observable<DataSourceCategoryComposite[]> {
    let httpParams = new HttpParams();
    if (dataSourceDomainKey != null) {
      httpParams = httpParams.append('data_source_domain_key', dataSourceDomainKey);
    }
    return this._analysisApiAccess.get<DataSourceCategoryComposite[]>('data-source-category-composite', {
      params: httpParams,
    });
  }

  public modelTypeListCache = this._cachingService.create(this, this.fetchModelTypeList);
  public fetchModelTypeList(): Observable<ModelTypeEntity[]> {
    return this._analysisApiAccess.get<ModelTypeEntity[]>('model-type');
  }

  public modelTypeCache = this._cachingService.create(this, this.fetchModelType);
  public fetchModelType(modelTypeUuid: string): Observable<ModelTypeEntity> {
    return this.modelTypeListCache.get().pipe(map((items) => items.find((i) => i.uuid === modelTypeUuid)));
  }

  public eventTypeCache = this._cachingService.create(this, this.fetchEventType);
  public fetchEventType(eventTypeUuid: string): Observable<EventTypeEntity> {
    return this._analysisApiAccess.get<EventTypeEntity>(`event-type/${eventTypeUuid}`);
  }

  public eventTypeListCache = this._cachingService.create(this, this.fetchEventTypeList);
  public fetchEventTypeList(): Observable<EventTypeEntity[]> {
    return this._analysisApiAccess.get<EventTypeEntity[]>('event-type');
  }
  public companyEventTypeListCache = new StableCache(
    'app.company_event_type',
    this,
    this.fetchCompanyEventTypeLists,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchCompanyEventTypeLists(): Observable<CompanyEventTypeEntity[]> {
    return this._analysisApiAccess.get<CompanyEventTypeEntity[]>('company-event-type');
  }

  public companyEventTypeCache = this._cachingService.create(this, this.fetchCompanyEventType);
  public fetchCompanyEventType(companyEventType: string): Observable<CompanyEventTypeEntity> {
    return this._analysisApiAccess.get<CompanyEventTypeEntity>(`company-event-type/${companyEventType}`);
  }

  public companyEventCompositeListCache = this._cachingService.create(this, this.fetchCompanyEventCompositeList);
  public fetchCompanyEventCompositeList(
    publishedFlag: boolean,
    limit = 100,
    pageOffset = 0,
    dontIncludeEnin: string[],
    appCustomerUuid: string = null,
    adminReviewFlag: boolean = null,
    onlyPrimary = true,
  ): Observable<SimpleCollection<CompanyEventBasicComposite>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('published_flag', String(publishedFlag));
    httpParams = httpParams.append('limit', String(limit));
    httpParams = httpParams.append('offset', String(pageOffset * limit));
    httpParams = httpParams.append('primary_flag', String(onlyPrimary));

    if (dontIncludeEnin && dontIncludeEnin.length > 0) {
      httpParams = httpParams.append('excluded_app_customer_uuids', dontIncludeEnin.join(','));
    }

    if (appCustomerUuid !== null) {
      httpParams = httpParams.append('app_customer_uuid', appCustomerUuid);
    }
    // check for null !important -> can be sent false
    if (adminReviewFlag !== null) {
      httpParams = httpParams.append('admin_review_flag', String(adminReviewFlag));
    }
    return this._applicationApiAccess.get<SimpleCollection<CompanyEventBasicComposite>>(
      'company-event-basic-composite-collection',
      { params: httpParams },
    );
  }

  public eventListCache = this._cachingService.create(this, this.fetchEventList);
  public fetchEventList(
    dataSourceCategoryKeys: DataSourceCategoryKey[],
    //dataSourceIdentifier: string,
    limit: number,
    personNameSearchQuery?: string,
    offset?: string,
    keywordQuery?: string,
    excludeKeywordQuery?: string,
    countFlag = false,
  ): Observable<EventEntity[]> {
    let httpParams = new HttpParams()
      .append('limit', limit)
      .append('data_source_category_identifiers', dataSourceCategoryKeys.join(','))
      .append('offset', offset);
    let httpHeaders = new HttpHeaders();
    if (personNameSearchQuery != null && personNameSearchQuery.length > 0) {
      httpParams = httpParams.append('person_name_search_query', personNameSearchQuery);
      httpHeaders = httpHeaders.set('Nk-Role-Keys', AppRoleKey.aml);
    }
    if (keywordQuery != null && keywordQuery.length > 0) {
      httpParams = httpParams.append('keyword_query', keywordQuery);
      httpHeaders = httpHeaders.set('Nk-Role-Keys', AppRoleKey.aml);
    }
    if (excludeKeywordQuery != null && excludeKeywordQuery.length > 0) {
      httpParams = httpParams.append('exclude_keyword_query', excludeKeywordQuery);
      httpHeaders = httpHeaders.set('Nk-Role-Keys', AppRoleKey.aml);
    }
    httpParams = httpParams.append('count_flag', String(countFlag));
    return this._analysisApiAccess.get<EventEntity[]>(
      'event',
      { params: httpParams, headers: httpHeaders },
      [
        AppFeatureKey.base,
        AppFeatureKey.events_news,
        AppFeatureKey.events_news_digital,
        AppFeatureKey.events_news_print,
        AppFeatureKey.company_risk_score,
        ...COUNTRY_FEATURES,
      ],
      [AppRoleKey.credit_check, AppRoleKey.norwegian_property_data],
    );
  }

  public eventCache = this._cachingService.create(this, this.fetchEvent);
  public fetchEvent(
    eventUuid: string,
    skipSensitiveDataClaim = false, // FOR THE ADMIN DIGEST REVIEW
  ): Observable<EventEntity> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([
        AppFeatureKey.base,
        AppFeatureKey.events_news,
        AppFeatureKey.payment_remark,
        AppFeatureKey.norwegian_property_data,
        AppFeatureKey.company_risk_score,
        ...COUNTRY_FEATURES,
      ]),
      this._sensitiveDataService.claimValidSensitiveDataTypeKeysByParams({ eventUuid: eventUuid }, [
        SensitiveDataTypeKey.payment_remark,
      ]),
    ]).pipe(
      switchMap(([validAppFeature, validSensitiveDataTypeKeys]) => {
        return this._analysisApiAccess.get<EventEntity>(
          `event/${eventUuid}`,
          {},
          validAppFeature,
          [AppRoleKey.credit_check, AppRoleKey.norwegian_property_data],
          skipSensitiveDataClaim
            ? []
            : this.filterValidSensitiveDataClaims(validAppFeature, validSensitiveDataTypeKeys),
        );
      }),
    );
  }

  public companyRiskScoreMeasureListCache = this._cachingService.create(this, this.fetchCompanyRiskScoreMeasureList);
  public fetchCompanyRiskScoreMeasureList(companyUuid: string): Observable<CompanyRiskScoreMeasureEntity[]> {
    return this._analysisApiAccess.get<CompanyRiskScoreMeasureEntity[]>(
      `company/${companyUuid}/company-risk-score-measure`,
      {},
      [AppFeatureKey.base, AppFeatureKey.company_risk_score, ...COUNTRY_FEATURES],
    );
  }

  public companyRiskCompositeListCache = this._cachingService.create(this, this.fetchCompanyRiskCompositeList);
  public fetchCompanyRiskCompositeList(
    companyUuid: string,
    companyRiskScoreModelUuid: string = null,
    latestMeasureFlag: boolean = null,
  ): Observable<CompanyRiskScoreCompositeEntity[]> {
    let params = new HttpParams();
    if (companyRiskScoreModelUuid) {
      params = params.append('company_risk_score_model_uuid', companyRiskScoreModelUuid);
    }
    if (latestMeasureFlag) {
      params = params.append('latest_measure_flag', String(latestMeasureFlag));
    }
    return this._analysisApiAccess.get<CompanyRiskScoreCompositeEntity[]>(
      `company/${companyUuid}/company-risk-score-composite`,
      { params: params },
      [AppFeatureKey.base, AppFeatureKey.company_risk_score, ...COUNTRY_FEATURES],
    );
  }
  public getPrioritizedCompanyRiskScoreModel(
    models: CompanyRiskScoreModelEntity[],
    priorities: CompanyRiskScoreModelPriorityEntity[],
  ): CompanyRiskScoreModelEntity {
    const prioritizedModels = priorities.sort((a, b) => {
      return a.model_rank - b.model_rank;
    });
    return models.find((a) => prioritizedModels[0].company_risk_score_model_uuid === a.uuid);
  }
  public streamPrioritizedCompanyRiskScoreListByCompanyUuidList(
    companyUuidList: string[],
  ): Observable<Dict<CompanyRiskScoreCompositeEntity[]>> {
    // Uses highest prioritized model.
    let companyRiskScoreModel: CompanyRiskScoreModelEntity | null = null;
    return forkJoin([
      this.companyRiskScoreModelListCache.getStableCache().pipe(first()),
      this.companyRiskScoreModelPriorityListCache.getStableCache().pipe(first()),
    ]).pipe(
      mergeMap((payload) => {
        const [modelList, priorityList] = payload;
        companyRiskScoreModel = this.getPrioritizedCompanyRiskScoreModel(modelList, priorityList);
        return from(companyUuidList);
      }),
      concatMap((companyUuid) => {
        return forkJoin([
          this.companyRiskCompositeListCache.get(companyUuid, companyRiskScoreModel.uuid, null),
          of(companyUuid),
        ]);
      }),
      mergeMap((payload) => {
        const companyUuid = payload[1];
        let companyRiskCompositeList = payload[0];

        companyRiskCompositeList = companyRiskCompositeList.sort((a, b) => {
          return (
            b.company_risk_score_measure.measure_timestamp.valueOf() -
            a.company_risk_score_measure.measure_timestamp.valueOf()
          );
        });

        return of({ [companyUuid]: companyRiskCompositeList });
      }),
    );
  }

  public updateCompanyEvent<T>(companyEventUuid: string, requestPayload: T): Observable<CompanyEventEntity> {
    return this._applicationApiAccess.patch<CompanyEventEntity>(
      `company-event/${companyEventUuid}`,
      requestPayload,
      {},
      [AppFeatureKey.base, AppFeatureKey.events_news, AppFeatureKey.norwegian_property_data],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public fetchCompanyEventCommonMeasureByCompanyEvent(
    companyEventUuid: string,
    skipSensitiveDataClaim = false, // used for digest admin review (admin_flag)
  ): Observable<CompanyEventCommonMeasureEntity[]> {
    return combineLatest([
      this._appFeatureService.claimValidAppFeatureKeys([
        AppFeatureKey.base,
        AppFeatureKey.events_news,
        AppFeatureKey.payment_remark,
        AppFeatureKey.norwegian_property_data,
        AppFeatureKey.company_risk_score,
      ]),
      this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check, AppRoleKey.norwegian_property_data]),
      skipSensitiveDataClaim
        ? of([])
        : this._sensitiveDataService.claimValidSensitiveDataTypeKeysByParams({ companyEventUuid: companyEventUuid }, [
            SensitiveDataTypeKey.payment_remark,
          ]),
    ]).pipe(
      switchMap(([validFeatures, validRoles, validSensitiveDataTypeKeys]) =>
        this._analysisApiAccess.get<CompanyEventCommonMeasureEntity[]>(
          `company-event/${companyEventUuid}/company-event-common-measure`,
          {},
          validFeatures,
          validRoles,
          this.filterValidSensitiveDataClaims(validFeatures, validSensitiveDataTypeKeys),
        ),
      ),
    );
  }

  public filterValidSensitiveDataClaims(
    claimedFeature: AppFeatureKey[],
    claimedSensitiveData: SensitiveDataTypeKey[],
  ): SensitiveDataTypeKey[] {
    return claimedSensitiveData.filter((sd) => {
      if (sd === SensitiveDataTypeKey.payment_remark && includes(claimedFeature, AppFeatureKey.payment_remark)) {
        return true;
      } else if (
        sd === SensitiveDataTypeKey.payment_remark &&
        !includes(claimedFeature, AppFeatureKey.payment_remark)
      ) {
        return false;
      }
      return true;
    });
  }

  public companyEventCommonModelListCache = this._cachingService.create(this, this.fetchCompanyEventCommonModelList);
  public fetchCompanyEventCommonModelList(): Observable<CompanyEventCommonModelEntity[]> {
    return this._analysisApiAccess.get<CompanyEventCommonModelEntity[]>('company-event-common-model');
  }

  public companyEventCommonGroupingListCache = this._cachingService.create(
    this,
    this.fetchCompanyEventCommonGroupingList,
  );
  public fetchCompanyEventCommonGroupingList(): Observable<CompanyEventCommonGroupingEntity[]> {
    return this._analysisApiAccess.get<CompanyEventCommonGroupingEntity[]>('company-event-common-grouping');
  }

  public companyEventCommonGroupingCache = this._cachingService.create(this, this.fetchCompanyEventCommonGrouping);
  public fetchCompanyEventCommonGrouping(
    companyEventCommonGroupingUuid: string,
  ): Observable<CompanyEventCommonGroupingEntity> {
    return this._analysisApiAccess.get<CompanyEventCommonGroupingEntity>(
      `company-event-common-grouping/${companyEventCommonGroupingUuid}`,
    );
  }

  public resolveCompanyRiskScoreGrouping(
    measure: CompanyRiskScoreMeasureEntity,
    allGroupings: CompanyRiskScoreGroupingEntity[],
  ): CompanyRiskScoreGroupingEntity {
    let groupings = allGroupings.filter(
      (g) => g.company_risk_score_model_uuid === measure.company_risk_score_model_uuid,
    );
    groupings = groupings.length > 0 ? groupings : allGroupings.filter((g) => g.company_risk_score_model_uuid === null);
    return this._resolveGrouping(measure, groupings) as CompanyRiskScoreGroupingEntity;
  }

  public resolveCompanyEventCommonGrouping(
    measure: CompanyEventCommonMeasureEntity,
    modelTypeUuid: string,
    allGrouping: CompanyEventCommonGroupingEntity[],
  ): CompanyEventCommonGroupingEntity {
    const groupingPerModelType = allGrouping.filter((a) => a.model_type_uuid === modelTypeUuid);
    if (groupingPerModelType.length === 0) {
      // This should not happen. All model types should have a set of groupings.
      console.error(
        'Tried to resolve grouping for a measure, but no grouping existed for said model type: ' +
          modelTypeUuid +
          ' and measure:',
        measure,
      );
      return null as CompanyEventCommonGroupingEntity;
    }
    return this._resolveGrouping(measure, groupingPerModelType) as CompanyEventCommonGroupingEntity;
  }

  private _resolveGrouping(
    measure: CompanyRiskScoreMeasureEntity | CompanyEventCommonMeasureEntity,
    specificGroupings: CompanyRiskScoreGroupingEntity[] | CompanyEventCommonGroupingEntity[],
  ): CompanyRiskScoreGroupingEntity | CompanyEventCommonGroupingEntity {
    let unsetGroup: CompanyRiskScoreGroupingEntity | CompanyEventCommonGroupingEntity;
    for (const grouping of specificGroupings) {
      const fromMissing = !grouping.from_measure_value && grouping.from_measure_value !== 0;
      const toMissing = !grouping.to_measure_value && grouping.to_measure_value !== 0;
      if (
        measure &&
        (measure.measure_value || measure.measure_value === 0) &&
        ((fromMissing && !toMissing) || grouping.from_measure_value <= measure.measure_value) &&
        ((!fromMissing && toMissing) || measure.measure_value < grouping.to_measure_value)
      ) {
        return grouping;
      } else if (fromMissing && toMissing) {
        unsetGroup = grouping;
      }
    }
    if (measure && measure.measure_class) {
      for (const grouping of specificGroupings) {
        if (grouping.group_key === measure.measure_class) {
          return grouping;
        }
      }
    }
    return unsetGroup;
  }

  public postCompanyEventAdminReview(
    payload: Partial<CompanyEventAdminReviewEntity>,
  ): Observable<CompanyEventAdminReviewEntity> {
    return this._applicationApiAccess.post<CompanyEventAdminReviewEntity>('company-event-admin-review', payload);
  }

  public getCompanyEventReviewByCompanyEvent(companyEventUuid: string): Observable<CompanyEventAdminReviewEntity[]> {
    return this._applicationApiAccess.get<CompanyEventAdminReviewEntity[]>(
      `company-event/${companyEventUuid}/company-event-admin-review`,
    );
  }

  public deleteCompanyEventAdminReview(companyEventAdminReviewUuid: string): Observable<CompanyEventAdminReviewEntity> {
    return this._applicationApiAccess.delete<CompanyEventAdminReviewEntity>(
      `company-event-admin-review/${companyEventAdminReviewUuid}`,
    );
  }

  public deleteCompanyEventCommonMeasure(
    companyEventCommonMeasureUuid: string,
  ): Observable<CompanyEventCommonMeasureEntity> {
    return this._applicationApiAccess.delete<CompanyEventCommonMeasureEntity>(
      `company-event-common-measure/${companyEventCommonMeasureUuid}`,
    );
  }

  public updateCompanyEventCommonMeasure(
    requestPayload: Partial<CompanyEventCommonMeasureEntity>,
    companyEventCommonMeasureUuid: string,
  ): Observable<CompanyEventCommonMeasureEntity> {
    // Send uuid to update.
    if (companyEventCommonMeasureUuid) {
      return this._applicationApiAccess.put<CompanyEventCommonMeasureEntity>(
        `company-event-common-measure/${companyEventCommonMeasureUuid}`,
        requestPayload,
      );
    } else {
      return this._applicationApiAccess.post<CompanyEventCommonMeasureEntity>(
        'company-event-common-measure',
        requestPayload,
      );
    }
  }

  // get one analysis per model type
  // important we only get one analysis per model type. where in resolveCompanyEventCommonAnalysisList we get all.
  public resolveCompanyEventCommonAnalysisListOnePerModelType(
    measures: CompanyEventCommonMeasureEntity[],
  ): Observable<CompanyEventCommonAnalysis[]> {
    // todo implement endpoints => companyEventCommonRuleListCache
    // Get one analysis per model type. Using the priority table.

    return forkJoin([
      this.companyEventCommonModelListCache.get().pipe(first()),
      this.companyEventCommonGroupingListCache.get().pipe(first()),
      this.companyEventCommonModelPriorityListCache.get().pipe(first()),
      this.modelTypeListCache.get().pipe(first()),
      this.companyEventCommonRuleListCache.get().pipe(first()),
    ]).pipe(
      mergeMap((payload) => {
        const [allModels, allGroupings, allPriorities, allModelTypes, allRules] = payload;
        const modelDict = arrayToDict(allModels, (a) => a.uuid);
        const sortedPriorities = allPriorities.sort((p1, p2) => (p1.model_rank < p2.model_rank ? -1 : 1));
        // Get the most prioritized measure within each model

        // there should only be one measure per model? right?
        // Get each model for each measure.
        // Model list is as long as measures list

        const outerModelList = measures.map((measure) => modelDict[measure.company_event_common_model_uuid]);
        if (outerModelList.length === 0) {
          return of(null);
        }
        const modelListByModelTypeDict = arrayToDictList(outerModelList, (a) => a.model_type_uuid);
        const prioritizedModelPerModelType: CompanyEventCommonModelEntity[] = [];
        for (const modelTypeUuid of dictToKeys(modelListByModelTypeDict)) {
          if (modelListByModelTypeDict[modelTypeUuid]) {
            const modelList = modelListByModelTypeDict[modelTypeUuid];
            const modelListUuids = modelListByModelTypeDict[modelTypeUuid].map((a) => a.uuid);
            const priority = sortedPriorities.find(
              (p) => modelListUuids.indexOf(p.company_event_common_model_uuid) >= 0,
            );
            let model;
            if (!priority) {
              // no priority found for any of the models in the modelType list.
              // they have the same priority, return any?
              model = modelList[0];
            } else {
              model = modelList.find((a) => a.uuid === priority.company_event_common_model_uuid);
            }
            prioritizedModelPerModelType.push(model);
          }
        }
        // Sorting is done.
        const analyses = prioritizedModelPerModelType.map((model) => {
          const measure = measures.find((m) => m.company_event_common_model_uuid === model.uuid);
          const priority = allPriorities.find((a) => a.company_event_common_model_uuid === model.uuid);
          const rule = allRules.find((r) => r.uuid === measure.company_event_common_rule_uuid);
          const modelType = allModelTypes.find((a) => a.uuid === model.model_type_uuid);

          return new CompanyEventCommonAnalysis(
            model,
            measure,
            this.resolveCompanyEventCommonGrouping(measure, model.model_type_uuid, allGroupings),
            rule,
            priority,
            modelType,
          );
        });
        return of(analyses);
      }), // mergeMap ends
    ); // pipe and forkJoin ends
  }

  public resolveCompanyRiskScoreAnalysisList(
    measures: CompanyRiskScoreMeasureEntity[],
  ): Observable<CompanyRiskScoreAnalysis[]> {
    if (measures === null) {
      return of(null);
    } else if (measures && measures.length === 0) {
      return of<CompanyRiskScoreAnalysis[]>([]);
    }
    return forkJoin([
      this.companyRiskScoreModelListCache.getStableCache().pipe(first()),
      this.companyRiskScoreGroupingListCache.get().pipe(first()),
      this.companyRiskScoreModelPriorityListCache.getStableCache().pipe(first()),
    ]).pipe(
      map((payload) => {
        const [allModels, allGroupings, allPriorities] = payload;
        return measures
          .map((measure) => {
            const model = allModels.find((m) => m.uuid === measure.company_risk_score_model_uuid);
            if (model && model.disabled_flag === false) {
              const priority = allPriorities
                .sort((p1, p2) => (p1.model_rank < p2.model_rank ? -1 : 1))
                .find((p) => p.company_risk_score_model_uuid === model.uuid);
              return new CompanyRiskScoreAnalysis(
                model,
                measure,
                this.resolveCompanyRiskScoreGrouping(measure, allGroupings),
                priority,
              );
            } else {
              return null;
            }
          })
          .filter((m) => m);
      }),
    );
  }

  public resolveWithDataCompanyEventCommonAnalysisList(
    measureList: CompanyEventCommonMeasureEntity[],
    allModels: CompanyEventCommonModelEntity[],
    allGroupings: CompanyEventCommonGroupingEntity[],
    allRules: CompanyEventCommonRuleEntity[],
    allPriorities: CompanyEventCommonModelPriorityEntity[],
    allModelTypes: ModelTypeEntity[],
  ): CompanyEventCommonAnalysis[] {
    if (measureList === undefined || measureList === null) {
      return [];
    }
    measureList = measureList.filter((m) => m !== null && m !== undefined);

    return measureList.map((measure) => {
      const priority = allPriorities.find(
        (p) => p.company_event_common_model_uuid === measure.company_event_common_model_uuid,
      );
      const rule = allRules.find((r) => r.uuid === measure.company_event_common_rule_uuid);
      const model = allModels.find((x) => x.uuid === measure.company_event_common_model_uuid);
      const modelType = allModelTypes.find((m) => m.uuid === model.model_type_uuid);
      const grouping = this.resolveCompanyEventCommonGrouping(measure, model.model_type_uuid, allGroupings);
      return new CompanyEventCommonAnalysis(model, measure, grouping, rule, priority, modelType);
    });
  }
  public resolveCompanyEventCommonAnalysisList(
    measures: CompanyEventCommonMeasureEntity[],
  ): Observable<CompanyEventCommonAnalysis[]> {
    if (measures === undefined || measures === null) {
      return of(null);
    } else if (measures && measures.length === 0) {
      return of<CompanyEventCommonAnalysis[]>([]);
    }
    measures = measures.filter((m) => m !== null && m !== undefined);
    return forkJoin([
      this.companyEventCommonModelListCache.get().pipe(first()),
      this.companyEventCommonGroupingListCache.get().pipe(first()),
      this.companyEventCommonModelPriorityListCache.get().pipe(first()),
      this.modelTypeListCache.get().pipe(first()),
      this.companyEventCommonRuleListCache.get().pipe(first()),
    ]).pipe(
      mergeMap((payload) => {
        const [allModels, allGroupings, allPriorities, allModelTypes, rules] = payload;
        const allRules = rules || [];
        const analysisCollection = measures
          .map((measure) => {
            const model = allModels.find((m) => m.uuid === measure.company_event_common_model_uuid);
            if (model) {
              const priority = allPriorities.find((p) => p.company_event_common_model_uuid === model.uuid);
              const modelType = allModelTypes.find((m) => m.uuid === model.model_type_uuid);
              return new CompanyEventCommonAnalysis(
                model,
                measure,
                this.resolveCompanyEventCommonGrouping(measure, model.model_type_uuid, allGroupings),
                allRules.find((r) => r.uuid === measure.company_event_common_rule_uuid),
                priority,
                modelType,
              );
            } else {
              return null;
            }
          })
          .filter((x) => x);
        return of(analysisCollection);
      }),
    );
  }

  public companyRiskScoreAnalysisCollectionByModelCache = this._cachingService.create(
    this,
    this.fetchCompanyRiskScoreAnalysisCollectionByModel,
  );
  public fetchCompanyRiskScoreAnalysisCollectionByModel(
    companyUuid: string,
  ): Observable<CompanyRiskScoreAnalysisListByModel> {
    return this.companyRiskScoreMeasureListCache.get(companyUuid).pipe(
      mergeMap((measures) => {
        return this.resolveCompanyRiskScoreAnalysisList(measures);
      }),
      map((analyses) => {
        const descMeasureComparisonFn = (a: CompanyRiskScoreAnalysis, b: CompanyRiskScoreAnalysis): -1 | 1 =>
          a.measure.measure_timestamp < b.measure.measure_timestamp ? -1 : 1;
        const ascMeasureComparisonFn = (a: CompanyRiskScoreAnalysis, b: CompanyRiskScoreAnalysis): number =>
          -descMeasureComparisonFn(a, b);
        const modelToLatestAnalysisDict = arrayToDict(analyses, (a) => a.model.uuid, ascMeasureComparisonFn);
        return new CompanyRiskScoreAnalysisListByModel(
          arrayToDictList(analyses, (a) => a.model.uuid, descMeasureComparisonFn),
          modelToLatestAnalysisDict,
          dictToValues(modelToLatestAnalysisDict).map((a) => a.model),
        );
      }),
    );
  }

  public fetchCompanyEventCompositeCollection(
    params: any, // TODO: _CompanyEventCompositeParams, There exists a similar interface somewhere. Merge?
  ): Observable<CompanyEventComposite[]> {
    const {
      customerUuid,
      companyUuid,
      watchlistUuid,
      companyBatchUuid,
      limit = 100,
      offset = 0,
      fromTimestamp: fromTimestamp,
      toTimestamp: toTimestamp,
      dataSourceUuidList,
      dataSourceCategoryList,
      keywordQuery,
      modelTypeUuid,
      groupKeyList,
      onlyPrioritizedAnalysesFlag,
    } = params;

    let httpParams = new HttpParams();

    if (!(customerUuid || watchlistUuid || companyBatchUuid || companyUuid)) {
      return throwError(
        () => 'Need one of the main filters customerUuid, watchlistUuid, companyBatchUuid, or companyUuid',
      );
    }

    httpParams = httpParams.append('limit', String(limit));

    if (customerUuid) {
      httpParams = httpParams.append('app_customer_identifier', customerUuid);
    }

    if (watchlistUuid) {
      httpParams = httpParams.append('watchlist_uuid', watchlistUuid);
    }

    if (companyBatchUuid) {
      httpParams = httpParams.append('company_batch_identifier', companyBatchUuid);
    }

    if (companyUuid) {
      httpParams = httpParams.append('company_identifier', companyUuid);
    }

    if (offset) {
      httpParams = httpParams.append('offset', String(offset));
    }

    if (fromTimestamp) {
      httpParams = httpParams.append('from_timestamp', fromTimestamp.toISOString());
    }
    if (toTimestamp) {
      httpParams = httpParams.append('to_timestamp', toTimestamp.toISOString());
    }

    if (dataSourceUuidList && dataSourceUuidList.length > 0) {
      httpParams = httpParams.append('data_source_uuids', dataSourceUuidList.join(','));
    }

    if (dataSourceCategoryList && dataSourceCategoryList.length > 0) {
      httpParams = httpParams.append('data_source_category_identifiers', dataSourceCategoryList.join(','));
    }

    if (keywordQuery) {
      httpParams = httpParams.append('keyword_query', String(keywordQuery));
    }
    if (modelTypeUuid) {
      httpParams = httpParams.append('model_type_uuid', String(modelTypeUuid));
    } else {
      throwError(() => new Error('modelTypeUuid is required'));
    }
    if (groupKeyList.length) {
      httpParams = httpParams.append('group_keys', groupKeyList.join(','));
    }
    if (onlyPrioritizedAnalysesFlag) {
      httpParams = httpParams.append('only_prioritized_analyses_flag', String(onlyPrioritizedAnalysesFlag));
    }
    let preferredCountry = [];

    return this._membershipGeoCountryService.appMembershipGeoCountryPreference$.pipe(
      mergeMap((membershipGeoCountryPreferredList) => {
        preferredCountry = membershipGeoCountryPreferredList.map((a) => a.uuid); // TODO: move preferred country to global

        if (preferredCountry && preferredCountry.length > 0) {
          httpParams = httpParams.append('geo_country_identifiers', preferredCountry.join(','));
        } else {
          httpParams = httpParams.append('geo_country_identifiers', 'NO');
        }
        return combineLatest([
          this._appFeatureService.claimValidAppFeatureKeys([
            AppFeatureKey.base,
            AppFeatureKey.events_news,
            AppFeatureKey.payment_remark,
            AppFeatureKey.norwegian_property_data,
            AppFeatureKey.company_risk_score,
            ...COUNTRY_FEATURES,
          ]),
          this._appRoleService.claimValidAppRoleKeys([AppRoleKey.credit_check, AppRoleKey.norwegian_property_data]),
          this._sensitiveDataService.claimValidSensitiveDataTypeKeysByCompany(companyUuid, [
            SensitiveDataTypeKey.payment_remark,
          ]),
        ]).pipe(
          switchMap(([validFeatureKeys, validRoleKeys, validSensitiveDataTypeKeys]) => {
            return this._analysisApiAccess.get<CompanyEventComposite[]>(
              '/company-event-composite',
              { params: httpParams },
              validFeatureKeys,
              validRoleKeys,
              this.filterValidSensitiveDataClaims(validFeatureKeys, validSensitiveDataTypeKeys),
            );
          }),
        );
      }),
    );
  }

}

interface _CompanyEventCompositeParams {
  customerUuid: string;
  companyUuid: string;
  watchlistUuid: string;
  companyBatchUuid: string;
  limit: number;
  offset: number;
  fromTimestamp: Date;
  toTimestamp: Date;
  dataSourceUuidList: string[];
  dataSourceCategoryList: string[];
  keywordQuery: string;
  modelTypeUuid: string;
  groupKeyList: string;
  onlyPrioritizedAnalysesFlag: string;
}
